import { PropsWithChildren, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import cn from 'classnames';
import { MdError } from 'react-icons/md';
import { TmdxAvatar } from '@ds';
import { stickyShadowStyles } from '@lp/features/Scheduler/constants';
import { ScheduleViolationTooltip } from '@lp/features/Scheduler/components';
import { ResourceViolationItem } from '@lp/features/Scheduler/types';
import { BaseResource } from '@/api/apis/resources/schedule';
import { getFullName } from '@/utils/helpers';

type UserProfileCellProps<R extends BaseResource> = PropsWithChildren<{
  user: R;
  violation?: ResourceViolationItem;
  titleSuffix?: ReactNode;
}>;

export const UserProfileCell = <R extends BaseResource>({
  user,
  violation,
  titleSuffix,
  children,
}: UserProfileCellProps<R>) => {
  const fullName = getFullName(user.firstName, user.lastName);

  return (
    <td
      className={cn(
        'sticky left-0 max-w-[220px] py-1.5 px-3 rounded-s-lg border-none z-3',
        violation ? 'bg-danger-80' : 'bg-inherit',
        stickyShadowStyles,
      )}
    >
      <div className="flex gap-2 items-center">
        <TmdxAvatar radius="lg" size="lg" src={user.profilePhoto} name={fullName} />
        <div className="flex flex-col gap-0.5 truncate">
          <div className="flex items-center gap-1">
            <Link to={`/admin/users/employees/${user.id}`} className="truncate hover:underline" target="_blank">
              <span className="text-base-5 text-m-m">{fullName}</span>
            </Link>
            {titleSuffix && <span className="shrink-0 text-base-20 text-m-m">{titleSuffix}</span>}
            {violation && (
              <ScheduleViolationTooltip items={violation}>
                <div>
                  <MdError className="text-danger-30" size={16} />
                </div>
              </ScheduleViolationTooltip>
            )}
          </div>
          {children}
        </div>
      </div>
    </td>
  );
};
